import { CookieConsentUsercentrics } from '@xing-com/cookie-consent-usercentrics';
import type { Route as RouteData } from '@xing-com/crate-core-router';
import { useLoginState } from '@xing-com/crate-hooks-use-login-state';
import { ScrollManager } from '@xing-com/crate-layout-scroll-memo-module';
import { NoSSR } from '@xing-com/crate-utils-no-ssr';
import {
  useHost,
  useLoginAppUrl,
  useMetadata,
  useXinglet,
  XingletLoader,
} from '@xing-com/crate-xinglet';
import { useRuntime } from '@xing-com/crate-xinglet/internal';
import { ActiveItemContext, useViewerData } from '@xing-com/hub';

import { AdobePageViewTracker } from './adobe-page-view-tracker/adobe-page-view-tracker';
import { Generic, NoAccessPage } from './error-components';
import { initTracking } from './init-tracking';
import { LayoutSwitcher } from './layout-switcher';
import { LoadingSkeleton } from './loading-skeleton';
import { PageViewTracker } from './page-view-tracker/page-view-tracker';
import { Zerofier } from './zerofier';

declare global {
  // eslint-disable-next-line no-var
  var DD_RUM: // eslint-disable-next-line @typescript-eslint/no-explicit-any
  { setGlobalContextProperty: (key: string, value: any) => void } | undefined;
}

// calls ttt.init and sets up the tracking environment
// should always be the first call in the root provider
const ttt = initTracking();

export const RouteXinglet: React.FC<{
  name: string;
  pageName?: string;
  trackingChannel?: string;
  nwtTrackingPage?: string;
  nwtTrackingSentBy?: string;
}> = ({
  name,
  pageName,
  trackingChannel,
  nwtTrackingPage,
  nwtTrackingSentBy,
}) => {
  const { ssr } = useMetadata(name);
  const { loading: loadingXinglet, error } = useXinglet(name);
  const { loading: loadingViewerData } = useViewerData();

  if (error) {
    return <Generic />;
  }

  if (loadingXinglet || (!ssr && loadingViewerData)) {
    return <LoadingSkeleton />;
  }

  return (
    <>
      <PageViewTracker page={nwtTrackingPage} sentBy={nwtTrackingSentBy} />
      <AdobePageViewTracker
        pageName={pageName}
        trackingChannel={trackingChannel}
      />
      <Zerofier />
      <XingletLoader name={name} key={name} error={<Generic />} />
    </>
  );
};

export type RouteProps = {
  path: string;
  route: RouteData;
};

export const Route: React.FC<RouteProps> = ({ route }) => {
  const runtime = useRuntime();
  const { contributes = {} } = runtime.metadataMap[route.xinglet] ?? {};
  const {
    activeItem,
    brazeSection,
    cacheMaxAge,
    disableCookieConsentBanner = false,
    layout,
    layoutLoggedIn,
    loginDestination,
    loginTracking,
    nwtTrackingPage,
    nwtTrackingSentBy,
    pageName,
    trackingChannel,
    visibleTo: {
      loggedIn = true,
      loggedOut = false,
      softLoggedIn = false,
    } = {},
  } = contributes;

  const visibleTo = {
    loggedIn,
    loggedOut,
    softLoggedIn,
  };

  // Set xinglet as global context for Datadog RUM events
  if (globalThis.DD_RUM) {
    globalThis.DD_RUM.setGlobalContextProperty('xinglet', {
      name: route.xinglet.replace('@xing-com/crate-', ''),
      pageName,
    });
  }

  const host = useHost();
  const loginState = useLoginState();
  const loginAppUrl = useLoginAppUrl();
  const { isLoggedIn, isLoggedOut, isSoftLoggedIn } = loginState;

  // available for logged in users only, redirect to login page
  if (isLoggedOut && !visibleTo.loggedOut) {
    return host.redirectToLogin();
  }
  if (isSoftLoggedIn && !visibleTo.softLoggedIn) {
    return host.redirectToLogin();
  }

  // path does not go to any xinglet (logged in + logged out)
  if (!visibleTo.loggedIn && !visibleTo.loggedOut) {
    return <NoAccessPage />;
  }
  // available for logged out users only
  if (isLoggedIn && !visibleTo.loggedIn) {
    return <NoAccessPage />;
  }

  if (host.isServer && isLoggedOut && cacheMaxAge) {
    host.setHeader(
      'cache-control',
      `max-age=0, s-maxage=${cacheMaxAge}, must-revalidate`
    );
  }

  const isBrowserEnvironment = typeof window !== 'undefined';
  const isSafari =
    isBrowserEnvironment &&
    /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  const isLoginHostname = loginAppUrl.includes(host.getHostname());
  const isSafariAndLoginHostname = isSafari && isLoginHostname;

  return (
    <>
      <CookieConsentUsercentrics
        disableBanner={disableCookieConsentBanner || isSafariAndLoginHostname}
      />
      <ActiveItemContext.Provider value={activeItem}>
        <LayoutSwitcher
          layout={(isLoggedIn ? (layoutLoggedIn ?? layout) : layout) ?? 'xing'}
          loginState={loginState}
          loginDestination={loginDestination}
          loginTracking={loginTracking}
        >
          <RouteXinglet
            name={route.xinglet}
            pageName={pageName}
            trackingChannel={trackingChannel}
            nwtTrackingPage={nwtTrackingPage}
            nwtTrackingSentBy={nwtTrackingSentBy}
          />
        </LayoutSwitcher>
      </ActiveItemContext.Provider>
      <NoSSR>
        <XingletLoader
          name="@xing-com/crate-marketing-braze"
          ttt={ttt}
          section={brazeSection}
        />
        <ScrollManager />
      </NoSSR>
    </>
  );
};
